import type { OrderBookEntry, BookState, BookSource } from '../../types';
import type { MessageCallbacks } from './types';
import { normalizeTimestamp } from './types';

export function handleOrderBook(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'ORDER_BOOK' && obj.secId && (obj.bids || obj.asks))) return false;

  const secId = (obj.secId || '').toUpperCase();

  const parseBids = (raw: any): OrderBookEntry[] => {
    if (Array.isArray(raw)) {
      return raw
        .map((e: any) => ({ type: '0' as const, price: Number(e.price), size: Number(e.size), id: e.orderId || e.order_id || e.id || undefined, source: e.source || undefined }))
        .filter(e => e.price > 0 && e.size > 0)
        .sort((a, b) => b.price - a.price);
    }
    if (raw && typeof raw === 'object') {
      return Object.entries(raw)
        .map(([p, s]) => ({ type: '0' as const, price: parseFloat(p), size: Number(s) }))
        .filter(e => e.price > 0 && e.size > 0)
        .sort((a, b) => b.price - a.price);
    }
    return [];
  };

  const parseAsks = (raw: any): OrderBookEntry[] => {
    if (Array.isArray(raw)) {
      return raw
        .map((e: any) => ({ type: '1' as const, price: Number(e.price), size: Number(e.size), id: e.orderId || e.order_id || e.id || undefined, source: e.source || undefined }))
        .filter(e => e.price > 0 && e.size > 0)
        .sort((a, b) => a.price - b.price);
    }
    if (raw && typeof raw === 'object') {
      return Object.entries(raw)
        .map(([p, s]) => ({ type: '1' as const, price: parseFloat(p), size: Number(s) }))
        .filter(e => e.price > 0 && e.size > 0)
        .sort((a, b) => a.price - b.price);
    }
    return [];
  };

  const bids = parseBids(obj.bids);
  const asks = parseAsks(obj.asks);

  if (secId === cb.selectedSymbol) {
    cb.setOrderBook({ bids, asks });

    const timestamp = normalizeTimestamp(obj.timestamp_iso || obj.timestamp);
    cb.lastUpdateBySecIdRef.current.set(secId, timestamp);
    cb.setLastUpdateTime(timestamp);

    if (bids.length > 0 && asks.length > 0) {
      cb.setCurrentPrice((bids[0].price + asks[0].price) / 2);
    } else if (bids.length > 0) {
      cb.setCurrentPrice(bids[0].price);
    } else if (asks.length > 0) {
      cb.setCurrentPrice(asks[0].price);
    }

    if (obj.analytics) {
      if (obj.analytics.midPrice != null && Number.isFinite(obj.analytics.midPrice)) {
        cb.setCurrentPrice(Number(obj.analytics.midPrice));
      } else if (obj.analytics.bestBid != null && obj.analytics.bestAsk != null) {
        cb.setCurrentPrice((Number(obj.analytics.bestBid) + Number(obj.analytics.bestAsk)) / 2);
      }
    }
  }

  // Update internal BookState
  const book: BookState = { bids: new Map(), asks: new Map() };
  bids.forEach(bid => {
    const key = `px:${bid.price}`;
    const ex = book.bids.get(key);
    book.bids.set(key, { price: bid.price, size: (ex?.size || 0) + bid.size });
  });
  asks.forEach(ask => {
    const key = `px:${ask.price}`;
    const ex = book.asks.get(key);
    book.asks.set(key, { price: ask.price, size: (ex?.size || 0) + ask.size });
  });
  // The brain sends a full top-of-book analytics block on EVERY ORDER_BOOK message. Until now
  // the client read it only to set currentPrice for the selected symbol and then threw it away,
  // so a multi-row board had no way to show the market's bid/ask. This write is outside the
  // selected-symbol guard, so it lands for every counter.
  // Derive provenance from the per-level `source` the wire already carries. Our own resting
  // orders (source UI) are excluded from the vote -- they are in the book but they are not
  // liquidity we are pricing against, and including them would let a large own-order make the
  // book look like it had a source it does not.
  const sourceCounts: Record<string, number> = {};
  for (const lvl of [...bids, ...asks]) {
    const src = String(lvl.source ?? 'UNTAGGED').toUpperCase();
    sourceCounts[src] = (sourceCounts[src] ?? 0) + 1;
  }
  // Our own resting orders are in this book but they are not liquidity we price against, so
  // they do not get a vote -- otherwise one large own-order could make a book look like it had
  // a source it does not.
  const votes = Object.entries(sourceCounts).filter(([k]) => k !== 'UI');
  votes.sort((a, b) => b[1] - a[1]);
  const known = new Set(['SIMULATOR', 'VPN', 'REAL_DSE']);
  let provenance: BookSource;
  if (votes.length === 0) {
    // No levels at all, or only our own. That is an EMPTY book, which is a different statement
    // from "we cannot tell where this came from" -- conflating them is what made every counter
    // read UNKNOWN.
    provenance = 'EMPTY';
  } else if (votes.length === 1) {
    provenance = known.has(votes[0][0]) ? (votes[0][0] as BookSource) : 'UNKNOWN';
  } else {
    provenance = 'MIXED';
  }

  if (obj.analytics) {
    const a = obj.analytics;
    // `Number(x) || 0` would turn a genuine null bestBid into 0 -- a book with no bid rendered
    // as a book bid at zero. Keep null as null.
    const num = (v: unknown): number | null => {
      if (v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    book.analytics = {
      provenance,
      sourceCounts,
      bestBid: num(a.bestBid),
      bestAsk: num(a.bestAsk),
      midPrice: num(a.midPrice),
      spread: num(a.spread),
      spreadBps: num(a.spreadBps),
      totalBidVolume: num(a.totalBidVolume) ?? 0,
      totalAskVolume: num(a.totalAskVolume) ?? 0,
      depthImbalance: num(a.depthImbalance) ?? 0,
      microprice: num(a.microprice),
      asOf: obj.timestamp || new Date().toISOString(),
    };
  }

  cb.booksBySecIdRef.current.set(secId, book);
  cb.setBookAnalyticsBySecId?.(prev => {
    const next = new Map(prev);
    if (book.analytics) next.set(secId, book.analytics);
    return next;
  });

  return true;
}
